<template>
    <div class="self-media-training">
        <!-- 平台选择区域 -->
        <div class="platform-selection">
            <div class="platform-buttons">
                <button v-for="platform in platformOptions" :key="platform.value" class="platform-button"
                    :class="{ active: selectedPlatform === platform.value }" @click="selectPlatform(platform.value)">
                    <span class="platform-icon">{{ platform.icon }}</span>
                    <span class="platform-name">{{ platform.label }}</span>
                </button>
            </div>
        </div>

        <!-- 账号管理区域 -->
        <div class="account-management">
            <!-- 在账号管理区域的 section-header 部分修改 -->
            <div class="section-header">
                <h3 class="section-title">账号管理</h3>
                <div>
                    <!-- 单主体模式提示 -->
                    <div v-if="licenseInfo.singleSubjectMode" class="single-subject-warning">
                        <el-alert :title="licenseInfo.message" type="warning" :closable="false" />
                    </div>
                    <!-- 正常模式显示 -->
                    <div v-else class="license-details">
                        <div class="license-item">
                            <span class="label">总授权数：</span>
                            <span class="value">{{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total }}</span>
                        </div>
                        <div class="license-item">
                            <span class="label">已使用：</span>
                            <span class="value">{{ licenseInfo.used }}</span>
                        </div>
                        <div class="license-item">
                            <span class="label">剩余可用：</span>
                            <span class="value">{{ licenseInfo.remaining === -1 ? '无限制' : licenseInfo.remaining
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 搜索和操作区域 -->
            <div class="operation-bar">
                <div class="search-section">
                    <el-input v-model="searchForm.ghz_name" placeholder="请输入账号名称" clearable size="large"
                        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>

                    <el-button type="primary" size="large" @click="handleSearch">
                        <el-icon>
                            <Search />
                        </el-icon>
                        搜索
                    </el-button>
                </div>

                <div class="action-buttons">

                    <!-- 注意事项按钮 -->
                    <el-button type="warning" size="large" @click="showPrecautionsDialog" class="precautions-btn">
                        <el-icon>
                            <Warning />
                        </el-icon>
                        注意事项
                    </el-button>

                    <el-button type="success" size="large" @click="openAddDialog">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        新增账号
                    </el-button>

                    <el-button type="info" size="large" @click="batchCheckAuthorization" :loading="batchCheckLoading">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        一键检测RPA授权
                    </el-button>
                </div>
            </div>

            <!-- 账号列表 -->
            <el-table :data="accountList" stripe style="width: 100%; margin-top: 20px" v-loading="loading"
                class="account-table">
                <el-table-column prop="ghz_name" label="账号名称" width="180" />
                <el-table-column label="平台类型" width="120">
                    <template #default="{ row }">
                        <el-tag :type="getPlatformTagType(row.field_type)">
                            {{ getPlatformText(row.field_type) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="授权方式" width="120">
                    <template #default="{ row }">
                        <template v-if="row.auth_method === 1 || row.auth_method === 2">
                            <el-tag :type="row.auth_method === 1 ? 'success' : 'warning'">
                                {{ getAuthMethodText(row.auth_method) }}
                            </el-tag>
                        </template>
                        <span v-else class="empty-value">-</span>
                    </template>
                </el-table-column>

                <el-table-column label="AppID/Secret" width="200">
                    <template #default="{ row }">
                        <div v-if="row.auth_method === 1">
                            <div>AppID: {{ row.ghz_appid || '未设置' }}</div>
                            <div>Secret: {{ row.ghz_secret ? '******' : '未设置' }}</div>
                        </div>
                        <div v-else class="empty-value">-</div>
                    </template>
                </el-table-column>

                <!-- 开放平台授权列 -->
                <el-table-column label="开放平台" width="120">
                    <template #default="{ row }">
                        <div v-if="row.auth_method === 2">
                            <el-button
                                v-if="!row.access_token || row.access_token === 'null' || row.access_token === '[]'"
                                type="primary" size="small" @click="handleOpenAuth(row)" :loading="authLoading[row.id]">
                                在线授权
                            </el-button>
                            <el-button v-else type="success" size="small" @click="handleOpenAuth(row)"
                                :loading="authLoading[row.id]">
                                重新授权
                            </el-button>
                        </div>
                        <span v-else class="empty-value">-</span>
                    </template>
                </el-table-column>

                <el-table-column label="RPA授权" width="220">
                    <template #default="{ row }">
                        <div class="rpa-auth-buttons">
                            <el-button v-if="!row.cookies || row.cookies === 'null' || row.cookies === '[]'"
                                type="primary" size="small" @click="startAuthorization(row)">
                                点击授权
                            </el-button>
                            <el-button v-else type="warning" size="small" @click="startAuthorization(row)">
                                重新授权
                            </el-button>

                            <el-button :disabled="!row.cookies || row.cookies === 'null' || row.cookies === '[]'"
                                type="success" size="small" @click="checkAuthorization(row)"
                                :loading="checkLoading[row.id]">
                                检测授权
                            </el-button>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="IP+端口" width="150">
                    <template #default="{ row }">
                        <span v-if="row.ip_port">{{ row.ip_port }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <el-table-column label="账号密码" width="180">
                    <template #default="{ row }">
                        <span v-if="row.ip_password" class="password-preview">
                            {{ row.ip_password }}
                        </span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <el-table-column prop="update_time" label="更新时间" width="180" />
                <el-table-column prop="create_time" label="创建时间" width="180" />

                <el-table-column label="操作" width="180" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)"
                            :disabled="licenseInfo.singleSubjectMode && hasSameTypeOtherAccounts(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            编辑
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchAccountList"
                    @current-change="fetchAccountList" />
            </div>
        </div>

        <!-- 注意事项对话框 -->
        <el-dialog v-model="precautionsDialogVisible" width="700px" :close-on-click-modal="false">
            <template #header>
                <div style="color: #ff7048; font-weight: bold; font-size: 18px;">
                    平台注意事项
                </div>
            </template>
            <div class="precautions-content">
                <el-alert type="info" :closable="false" style="margin-bottom: 20px;">
                    <template #title>
                        <span style="font-weight: bold;">通用注意事项：</span>
                    </template>
                    <div class="common-precautions">
                        <p>1. 每个平台，请先手动发一条内容，再进行授权测试！</p>
                        <p>2. 每个平台大部分都需要实名认证，请先完成认证！</p>
                        <p>3. 百家号每天最多只能发布15篇内容！</p>
                        <p>4. 知乎发布频繁会超限，连续发5篇可能会提示频繁！</p>
                    </div>
                </el-alert>

                <div class="platform-links">
                    <div v-for="platform in platformPrecautions" :key="platform.value" class="platform-link-item">
                        <div class="platform-header">
                            <span class="platform-icon">{{ platform.icon }}</span>
                            <span class="platform-name">{{ platform.label }}</span>
                        </div>
                        <div class="platform-link">
                            <a :href="platform.link" target="_blank" rel="noopener noreferrer">
                                <el-icon>
                                    <Link />
                                </el-icon>
                                {{ platform.link }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <template #footer>
                <el-button type="primary" @click="precautionsDialogVisible = false">我知道了</el-button>
            </template>
        </el-dialog>

        <!-- 新增/编辑账号对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
            <el-form ref="accountForm" :model="accountForm" :rules="formRules" label-width="120px">
                <el-form-item label="账号名称" prop="ghz_name">
                    <el-input v-model="accountForm.ghz_name" placeholder="请输入账号名称" size="large" />
                </el-form-item>

                <el-form-item label="平台类型" prop="field_type">
                    <el-select v-model="accountForm.field_type" placeholder="请选择平台类型" size="large"
                        @change="handleAccountTypeChange">
                        <el-option v-for="platform in platformOptions" :key="platform.value" :label="platform.label"
                            :value="platform.value" />
                    </el-select>
                </el-form-item>

                <!-- 仅当选择微信公众号时显示授权方式 -->
                <el-form-item v-if="accountForm.field_type === 6" label="授权方式" prop="auth_method">
                    <el-select v-model="accountForm.auth_method" placeholder="请选择授权方式" size="large">
                        <el-option :label="'密钥授权'" :value="1" />
                        <el-option :label="'开放平台'" :value="2" />
                    </el-select>
                </el-form-item>

                <!-- 仅当选择微信公众号且授权方式为密钥授权时显示AppID和Secret -->
                <template v-if="accountForm.field_type === 6 && accountForm.auth_method === 1">
                    <el-form-item label="AppID" prop="ghz_appid">
                        <el-input v-model="accountForm.ghz_appid" placeholder="请输入账号AppID" size="large" />
                    </el-form-item>

                    <el-form-item label="Secret" prop="ghz_secret">
                        <el-input v-model="accountForm.ghz_secret" placeholder="请输入账号Secret" size="large"
                            show-password />
                    </el-form-item>
                </template>

                <!-- 抖音密码输入框 -->
                <el-form-item v-if="accountForm.field_type === 1" label="抖音密码" prop="dy_password">
                    <el-input v-model="accountForm.dy_password" placeholder="请输入抖音密码" size="large" type="password"
                        show-password />
                    <div class="form-tip">密码必须正确，否则可能导致发送失败！</div>
                </el-form-item>

                <el-form-item label="IP+端口">
                    <el-input v-model="accountForm.ip_port" placeholder="格式：232.33.1.1:16817（可选）" size="large" />
                    <div class="form-tip">非必填项，可留空</div>
                </el-form-item>

                <el-form-item label="账号密码">
                    <el-input v-model="accountForm.ip_password" placeholder="格式：账号:密码，如：34123:55332（可选）" size="large"
                        show-password />
                    <div class="form-tip">非必填项，可留空</div>
                </el-form-item>

                <el-form-item label="备注信息">
                    <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitAccountForm">确认</el-button>
            </template>
        </el-dialog>

        <!-- 授权二维码对话框 -->
        <el-dialog v-model="qrDialogVisible" title="账号授权" width="400px" :close-on-click-modal="false">
            <div class="qr-code-container">
                <div v-if="qrLoading" class="loading-container">
                    <el-icon class="is-loading" size="30">
                        <Loading />
                    </el-icon>
                    <span>正在获取二维码...</span>
                </div>

                <template v-else>
                    <img v-if="qrCodeBase64" :src="qrCodeBase64" alt="账号授权二维码" class="qr-code-image" />

                    <!-- 验证码输入区域 -->
                    <div v-if="showVerificationCode" class="verification-code-section">
                        <el-alert title="需要短信验证码" type="warning" :closable="false" style="margin-bottom: 15px;" />
                        <el-input v-model="verificationCode" :placeholder="'请输入验证码 (' + verificationElement + ')'"
                            clearable @keyup.enter="confirmAuthorization" />
                    </div>

                    <!-- 密码输入区域 -->
                    <div v-if="showPasswordInput" class="password-section">
                        <el-alert title="需要输入密码" type="warning" :closable="false" style="margin-bottom: 15px;" />
                        <el-input v-model="password" placeholder="请输入密码" type="password" show-password clearable
                            @keyup.enter="confirmAuthorization" />
                    </div>
                </template>

                <div class="qr-instructions">
                    <p v-if="!showVerificationCode && !showPasswordInput" class="auth-instruction">
                        请使用 <strong>{{ getPlatformName(currentAuthRow?.field_type) }}</strong> APP 扫描上方二维码授权账号
                    </p>
                    <p v-else-if="showVerificationCode">请输入收到的短信验证码</p>
                    <p v-else-if="showPasswordInput">请输入账号密码</p>
                    <p>完成后点击下方确认按钮</p>
                </div>
            </div>

            <template #footer>
                <el-button @click="qrDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmAuthorization" :loading="confirmLoading"
                    :disabled="!qrCodeBase64 || (showVerificationCode && !verificationCode) || (showPasswordInput && !password)">
                    {{ getConfirmButtonText() }}
                </el-button>
            </template>
        </el-dialog>

        <!-- 编辑AppID对话框 -->
        <el-dialog v-model="editAppIdDialogVisible" title="编辑AppID" width="500px">
            <el-form ref="editAppIdFormRef" :model="editAppIdForm" :rules="editAppIdRules" label-width="120px">
                <el-form-item label="AppID" prop="ghz_appid">
                    <el-input v-model="editAppIdForm.ghz_appid" placeholder="请输入开放平台AppID" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="editAppIdDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitEditAppId" :loading="editAppIdLoading">
                    确认
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import {
    Search, Plus, Edit, Delete, Loading, Refresh, Warning, Link
} from '@element-plus/icons-vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
    name: 'SelfMediaTraining',
    components: { Search, Plus, Edit, Delete, Loading, Refresh, Warning, Link },
    data() {
        // IP端口格式验证（非必填）
        const validateIpPort = (rule, value, callback) => {
            if (value && !/^(\d{1,3}\.){3}\d{1,3}:\d{1,5}$/.test(value)) {
                callback(new Error('格式错误，示例：192.168.1.1:8080'));
            } else {
                callback();
            }
        };

        // 账号密码格式验证（非必填）
        const validateAccountPassword = (rule, value, callback) => {
            if (value && !/^[^:]+:[^:]+$/.test(value)) {
                callback(new Error('格式错误，示例：username:password'));
            } else {
                callback();
            }
        };

        return {
            selectedPlatform: 1, // 默认选中抖音
            loading: false,
            dialogVisible: false,
            dialogTitle: '新增账号',
            isEditMode: false,
            qrDialogVisible: false,
            qrLoading: false,
            confirmLoading: false,
            qrCodeBase64: '',
            currentAuthRow: null,
            batchCheckLoading: false,
            editAppIdDialogVisible: false,
            editAppIdLoading: false,

            // 平台选项 - 使用 field_type 字段
            platformOptions: [
                { value: 1, label: '抖音', icon: '🎵' },
                { value: 2, label: '快手', icon: '⚡' },
                { value: 3, label: '小红书', icon: '📕' },
                // { value: 4, label: 'B站', icon: '📺' },
                { value: 5, label: '视频号', icon: '📹' },
                // { value: 6, label: '微信公众号', icon: '💬' },
                { value: 7, label: '今日头条', icon: '📰' },
                { value: 8, label: '百家号', icon: '🔍' },
                { value: 9, label: '知乎', icon: '❓' },
                // { value: 10, label: '百度文库', icon: '📚' }
            ],

            precautionsDialogVisible: false,
            platformPrecautions: [
                { value: 1, label: '抖音', icon: '🎵', link: 'https://creator.douyin.com/' },
                { value: 2, label: '快手', icon: '⚡', link: 'https://cp.kuaishou.com/profile' },
                { value: 3, label: '小红书', icon: '📕', link: 'https://creator.xiaohongshu.com/login' },
                { value: 5, label: '视频号', icon: '📹', link: 'https://channels.weixin.qq.com/' },
                { value: 7, label: '今日头条', icon: '📰', link: 'https://mp.toutiao.com/' },
                { value: 8, label: '百家号', icon: '🔍', link: 'https://baijiahao.baidu.com/builder/rc/home' },
                { value: 9, label: '知乎', icon: '❓', link: 'https://www.zhihu.com/creator' }
            ],
            // 搜索表单
            searchForm: {
                ghz_name: ''
            },

            // 账号表单 - 使用原有字段
            accountForm: {
                id: null,
                ghz_name: '',
                field_type: 1,
                auth_method: 3, // 默认RPA授权
                ghz_appid: '',
                ghz_secret: '',
                dy_password: '',
                ip_port: '',
                ip_password: '',
                notes: ''
            },

            // 编辑AppID表单
            editAppIdForm: {
                id: null,
                store_id: null,
                user_id: null,
                ghz_appid: ''
            },
            editAppIdRules: {
                ghz_appid: [
                    { required: true, message: '请输入AppID', trigger: 'blur' }
                ]
            },

            // 表单验证规则
            formRules: {
                ghz_name: [
                    { required: true, message: '请输入账号名称', trigger: 'blur' }
                ],
                field_type: [
                    { required: true, message: '请选择平台类型', trigger: 'change' }
                ],
                auth_method: [
                    {
                        required: () => this.accountForm.field_type === 6,
                        message: '请选择授权方式',
                        trigger: 'change'
                    }
                ],
                ghz_appid: [
                    {
                        required: () => this.accountForm.field_type === 6 && this.accountForm.auth_method === 1,
                        message: '请输入账号AppID',
                        trigger: 'blur'
                    }
                ],
                ghz_secret: [
                    {
                        required: () => this.accountForm.field_type === 6 && this.accountForm.auth_method === 1,
                        message: '请输入账号Secret',
                        trigger: 'blur'
                    }
                ],
                ip_port: [
                    { validator: validateIpPort, trigger: 'blur' }
                ],
                ip_password: [
                    { validator: validateAccountPassword, trigger: 'blur' }
                ],
                dy_password: [
                    {
                        required: () => this.accountForm.field_type === 1,
                        message: '请输入抖音密码',
                        trigger: 'blur'
                    }
                ]
            },

            // 账号列表
            accountList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 授权信息
            licenseInfo: {
                total: 0,
                used: 0,
                remaining: 0
            },

            // 加载状态
            checkLoading: {},
            authLoading: {},

            // 授权相关
            webId: '',
            fingerprint: '',
            showVerificationCode: false,
            verificationCode: '',
            verificationElement: '',
            showPasswordInput: false,
            password: ''
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('store_user_info') || '{}')
        },
        userId() {
            return this.userInfo.id
        },
        storeId() {
            return this.userInfo.store_id
        }
    },
    mounted() {
        this.fetchAccountList()
        this.fetchLicenseInfo()
    },
    methods: {
        // 选择平台
        selectPlatform(platform) {
            this.selectedPlatform = platform
            this.fetchAccountList()
        },

        // 显示注意事项弹窗
        showPrecautionsDialog() {
            this.precautionsDialogVisible = true
        },

        // 获取平台显示文本
        getPlatformText(type) {
            const platform = this.platformOptions.find(p => p.value === type)
            return platform ? platform.label : '未知平台'
        },

        // 获取平台标签类型
        getPlatformTagType(type) {
            const typeMap = {
                1: 'danger',    // 抖音 - 红色
                2: 'warning',   // 快手 - 橙色
                3: 'danger',    // 小红书 - 红色
                4: 'info',      // B站 - 青色
                5: 'success',   // 视频号 - 绿色
                6: 'success',   // 微信公众号 - 绿色
                7: 'danger',          // 今日头条 - 默认
                8: 'warning',   // 百家号 - 橙色
                9: 'info',      // 知乎 - 青色
                10: 'success'   // 百度文库 - 绿色
            }
            return typeMap[type] || 'default'
        },

        // 获取平台名称
        getPlatformName(type) {
            return this.getPlatformText(type)
        },

        // 获取授权方式文本
        getAuthMethodText(method) {
            const methods = {
                1: '密钥授权',
                2: '开放平台'
            };
            return methods[method] || '-';
        },

        // 获取确认按钮文本
        getConfirmButtonText() {
            if (this.showVerificationCode) return '提交验证码'
            if (this.showPasswordInput) return '提交密码'
            return '确认授权'
        },

        // 平台类型变更处理
        handleAccountTypeChange(type) {
            // 微信公众号类型时，设置auth_method为1（密钥授权）
            if (type === 6) {
                this.accountForm.auth_method = 1;
            } else {
                // 其他平台类型时，设置auth_method为3（RPA授权）
                this.accountForm.auth_method = 3;
                this.accountForm.ghz_appid = '';
                this.accountForm.ghz_secret = '';
            }

            // 重置相关字段的验证状态
            if (this.$refs.accountForm) {
                this.$refs.accountForm.clearValidate(['auth_method', 'ghz_appid', 'ghz_secret', 'dy_password']);
            }
        },

        // 打开新增对话框
        openAddDialog() {
            // 检查授权数量
            if (this.licenseInfo.total !== -1 && this.licenseInfo.remaining <= 0) {
                ElMessage.warning('授权账号数已满，请联系管理员充值！')
                return
            }

            this.dialogTitle = '新增账号'
            this.isEditMode = false
            this.accountForm = {
                id: null,
                ghz_name: '',
                field_type: this.selectedPlatform,
                auth_method: this.selectedPlatform === 6 ? 1 : 3,
                ghz_appid: '',
                ghz_secret: '',
                dy_password: '',
                ip_port: '',
                ip_password: '',
                notes: ''
            }
            this.dialogVisible = true
        },

        // 添加检查同类型其他账号的方法
        hasSameTypeOtherAccounts(row) {
            if (!this.licenseInfo.singleSubjectMode) {
                return false;
            }
            const sameTypeOtherAccounts = this.accountList.filter(item =>
                item.field_type === row.field_type && item.id !== row.id
            );
            return sameTypeOtherAccounts.length > 0;
        },

        // 修改打开编辑对话框的方法，改为检查同类型账号
        openEditDialog(row) {
            // 在单主体模式下，检查同类型的账号数量
            if (this.licenseInfo.singleSubjectMode) {
                // 检查同类型的其他账号数量
                const sameTypeOtherAccounts = this.accountList.filter(item =>
                    item.field_type === row.field_type && item.id !== row.id
                );

                if (sameTypeOtherAccounts.length > 0) {
                    const platformName = this.getPlatformText(row.field_type);
                    ElMessage.error(`单主体模式下，${platformName}类型的账号已存在其他账号，请先删除同类型的其他账号！`);
                    return;
                }
            }

            this.dialogTitle = '编辑账号'
            this.isEditMode = true
            this.accountForm = {
                id: row.id,
                ghz_name: row.ghz_name,
                field_type: row.field_type,
                auth_method: row.auth_method,
                ghz_appid: row.ghz_appid || '',
                ghz_secret: row.ghz_secret || '',
                dy_password: row.dy_password || '',
                ip_port: row.ip_port || '',
                ip_password: row.ip_password || '',
                notes: row.notes || ''
            }
            this.dialogVisible = true
        },

        // 提交账号表单
        // 修改 submitAccountForm 方法中的错误处理
        submitAccountForm() {
            this.$refs.accountForm.validate(async (valid) => {
                if (valid) {
                    try {
                        // 非微信公众号类型时，设置auth_method为3
                        if (this.accountForm.field_type !== 6) {
                            this.accountForm.auth_method = 3;
                        }

                        // 如果不是抖音类型，清除抖音密码
                        if (this.accountForm.field_type !== 1) {
                            this.accountForm.dy_password = '';
                        }

                        const action = this.isEditMode ? 'update' : 'add'
                        const payload = {
                            ...this.accountForm,
                            store_id: this.storeId,
                            user_id: this.userId
                        }

                        // 如果授权方式不是密钥授权，清除AppID和Secret
                        if (payload.auth_method !== 1) {
                            payload.ghz_appid = '';
                            payload.ghz_secret = '';
                        }

                        const response = await axios.post(
                            `${config.BASE_URL}/store/api/gzh_auth_management.php`,
                            {
                                action,
                                ...payload
                            }
                        )

                        if (response.data.success) {
                            ElMessage.success(this.isEditMode ? '更新成功' : '添加成功')
                            this.dialogVisible = false
                            this.fetchAccountList()
                            this.fetchLicenseInfo() // 刷新授权信息
                        } else {
                            // 改进错误提示，优先使用 error 字段，然后是 message 字段
                            const errorMsg = response.data.error || response.data.message || '操作失败'
                            ElMessage.error(errorMsg)
                        }
                    } catch (error) {
                        // 改进错误处理，处理不同的错误响应格式
                        let errorMsg = '操作失败';

                        if (error.response && error.response.data) {
                            const errorData = error.response.data;
                            // 优先使用 error 字段，然后是 message 字段，最后是 reason 字段
                            errorMsg = errorData.error || errorData.message || errorData.reason || '操作失败';

                            // 如果是单主体模式错误，显示更友好的提示
                            if (errorData.single_subject_mode) {
                                errorMsg = errorData.message || errorData.error || '单主体模式下操作受限';
                            }
                        } else if (error.message) {
                            errorMsg = error.message;
                        }

                        ElMessage.error(`操作失败: ${errorMsg}`)
                        console.error('表单提交错误:', error)
                    }
                }
            })
        },

        // 获取账号列表
        async fetchAccountList() {
            this.loading = true
            try {
                const params = {
                    action: this.searchForm.ghz_name ? 'search' : 'list',
                    store_id: this.storeId,
                    user_id: this.userId,
                    field_type: this.selectedPlatform,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                }

                if (this.searchForm.ghz_name) {
                    params.ghz_name = this.searchForm.ghz_name
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/store/api/gzh_auth_management.php`,
                    params
                )

                if (data.data) {
                    this.accountList = data.data
                    this.pagination.total = data.pagination?.total || data.data.length
                }
            } catch (error) {
                ElMessage.error('获取账号列表失败')
                console.error('获取账号列表错误:', error)
            } finally {
                this.loading = false
            }
        },
        // 修改 fetchLicenseInfo 方法
        async fetchLicenseInfo() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/store/api/gzh_auth_management.php`,
                    {
                        action: 'license_info',
                        store_id: this.storeId,
                        user_id: this.userId
                    }
                )

                if (response.data && !response.data.error) {
                    // 检查是否为单主体模式
                    if (response.data.single_subject_mode) {
                        this.licenseInfo = {
                            total: 1,
                            used: 0,
                            remaining: 1,
                            singleSubjectMode: true,
                            message: response.data.message
                        }
                        // 显示单主体模式提示
                        this.$message.warning(response.data.message)
                    } else {
                        this.licenseInfo = {
                            total: response.data.total || 0,
                            used: response.data.used || 0,
                            remaining: response.data.remaining || 0,
                            singleSubjectMode: false
                        }
                    }
                }
            } catch (error) {
                console.error('获取授权信息失败:', error)
            }
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1
            this.fetchAccountList()
        },

        // 开始授权流程
        async startAuthorization(row) {
            this.currentAuthRow = row
            this.qrDialogVisible = true
            this.qrLoading = true
            this.qrCodeBase64 = ''
            this.showVerificationCode = false
            this.showPasswordInput = false
            this.verificationCode = ''
            this.password = ''

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/store/api/gzh_auth_cookies.php`,
                    {
                        action: 'get_qr',
                        id: this.currentAuthRow.id,
                        store_id: this.storeId,
                        user_id: this.userId,
                        field_type: row.field_type
                    }
                )

                if (response.data.status === 'success') {
                    this.qrCodeBase64 = response.data.base64
                    this.webId = response.data.web_id
                    this.fingerprint = response.data.fingerprint
                } else {
                    ElMessage.error(response.data.error || '获取二维码失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '获取二维码失败'
                ElMessage.error(errorMsg)
                console.error('获取二维码错误:', error)
            } finally {
                this.qrLoading = false
            }
        },

        // 确认授权
        async confirmAuthorization() {
            try {
                this.confirmLoading = true

                const payload = {
                    action: 'update_cookies',
                    web_id: this.webId,
                    fingerprint: this.fingerprint,
                    id: this.currentAuthRow.id,
                    store_id: this.storeId,
                    user_id: this.userId,
                    field_type: this.currentAuthRow.field_type
                }

                // 添加验证码或密码参数
                if (this.showVerificationCode) {
                    payload.verification_code = this.verificationCode
                }
                if (this.showPasswordInput) {
                    payload.password = this.password
                }

                const response = await axios.post(
                    `${config.BASE_URL}/store/api/gzh_auth_cookies.php`,
                    payload
                )

                if (response.data.status === 'success') {
                    ElMessage.success('授权成功')
                    this.qrDialogVisible = false
                    this.fetchAccountList()
                }
                // 处理需要验证码的情况
                else if (response.data.status === 'verification_required') {
                    this.showVerificationCode = true
                    this.verificationElement = response.data.verification_element || '#button-input'
                    ElMessage.warning('需要短信验证码才能继续')
                }
                // 处理需要密码的情况
                else if (response.data.status === 'password_required') {
                    this.showPasswordInput = true
                    ElMessage.warning('需要输入密码才能继续')
                }
                else {
                    ElMessage.error(response.data.message || '授权失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '授权失败'
                ElMessage.error(errorMsg)
                console.error('授权错误:', error)
            } finally {
                this.confirmLoading = false
            }
        },

        // 处理开放平台授权
        handleOpenAuth(row) {
            // 显示加载状态
            this.authLoading = { ...this.authLoading, [row.id]: true };

            // 构建授权URL - 添加 user_id 参数
            const authUrl = `${config.BASE_URL}/weixin/get_auth_url.php?id=${row.id}&store_id=${this.storeId}&user_id=${this.userId}`;

            // 在新标签页打开授权页面
            const authWindow = window.open(authUrl, '_blank');

            // 定时检查授权状态
            this.checkAuthStatus(row.id, authWindow);
        },

        // 检查授权状态
        async checkAuthStatus(id) {
            let interval;

            try {
                // 每2秒检查一次授权状态
                interval = setInterval(async () => {
                    try {
                        // 检查授权是否成功
                        const response = await axios.get(
                            `${config.BASE_URL}/weixin/check_auth_status.php?id=${id}`
                        );

                        // 处理服务器响应
                        if (response.data.success === true) {
                            // 授权成功 - 停止轮询
                            clearInterval(interval);
                            this.authLoading = { ...this.authLoading, [id]: false };

                            // 提示用户
                            ElMessage.success(`账号 ${response.data.appid} 授权已完成`);

                            // 刷新数据
                            this.fetchAccountList();
                        }
                    } catch (error) {
                        // 请求失败的处理
                        console.error('检查授权状态请求失败:', error);
                    }
                }, 2000);

                // 设置10分钟超时
                setTimeout(() => {
                    if (interval) {
                        clearInterval(interval);
                        this.authLoading = { ...this.authLoading, [id]: false };
                        ElMessage.error('授权流程未在10分钟内完成');
                    }
                }, 10 * 60 * 1000);
            } catch (error) {
                this.authLoading = { ...this.authLoading, [id]: false };
                ElMessage.error('授权请求发送失败: ' + error.message);
            }
        },

        // 检测单个授权
        async checkAuthorization(row) {
            this.checkLoading = { ...this.checkLoading, [row.id]: true }

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/store/api/update_cookies_by_ids.php`,
                    {
                        ids: [row.id],
                        field_type: row.field_type,
                        store_id: this.storeId,
                        user_id: this.userId
                    }
                )

                if (response.data.success) {
                    const result = response.data.results.find(r => r.id === row.id);
                    if (result) {
                        if (result.status === 'success') {
                            ElMessage.success(`ID ${row.id} 授权有效，已更新cookies`);
                        } else if (result.status === 'cooldown') {
                            ElMessage.warning(`ID ${row.id} 处于冷却期，${result.next_available}后可更新`);
                        } else {
                            ElMessage.warning(`ID ${row.id} ${result.message}`);
                        }
                    }
                } else {
                    ElMessage.error(response.data.error || '检测授权失败');
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '检测授权失败'
                ElMessage.error(errorMsg)
            } finally {
                this.checkLoading = { ...this.checkLoading, [row.id]: false }
                this.fetchAccountList()
            }
        },

        // 批量检测授权
        async batchCheckAuthorization() {
            const validIds = this.accountList
                .filter(item => item.cookies && item.cookies !== 'null' && item.cookies !== '[]')
                .map(item => ({
                    id: item.id,
                    field_type: item.field_type
                }))

            if (validIds.length === 0) {
                ElMessage.warning('没有可检测的授权账号')
                return
            }

            try {
                this.batchCheckLoading = true

                const response = await axios.post(
                    `${config.BASE_URL}/store/api/update_cookies_by_ids.php`,
                    {
                        ids: validIds,
                        store_id: this.storeId,
                        user_id: this.userId
                    }
                )

                if (response.data.success) {
                    // 统计结果
                    const stats = {
                        success: 0,
                        cooldown: 0,
                        error: 0
                    };

                    response.data.results.forEach(result => {
                        if (result.status === 'success') stats.success++;
                        else if (result.status === 'cooldown') stats.cooldown++;
                        else stats.error++;
                    });

                    ElMessage.success(`批量检测完成：成功: ${stats.success}个, 冷却中: ${stats.cooldown}个, 失败: ${stats.error}个`);
                } else {
                    ElMessage.error(response.data.error || '批量检测失败');
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '批量检测失败'
                ElMessage.error(errorMsg)
            } finally {
                this.batchCheckLoading = false
                this.fetchAccountList()
            }
        },

        // 打开编辑AppID对话框
        openEditAppIdDialog(row) {
            this.editAppIdForm = {
                id: row.id,
                store_id: this.storeId,
                user_id: this.userId,
                ghz_appid: row.ghz_appid || ''
            };
            this.editAppIdDialogVisible = true;
        },

        // 提交AppID更新
        async submitEditAppId() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/store/api/gzh_auth_management.php`,
                    {
                        action: 'update_appid',
                        ...this.editAppIdForm
                    }
                );

                if (response.data.error) {
                    ElMessage.error(response.data.error || '更新失败');
                } else if (response.data.success) {
                    ElMessage.success('AppID更新成功');
                    this.editAppIdDialogVisible = false;
                    this.fetchAccountList();
                }
            } catch (error) {
                let errorMsg = '更新失败';
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error;
                }
                ElMessage.error(errorMsg);
                console.error('更新AppID错误:', error);
            }
        },

        // 删除账号
        async handleDelete(id) {
            try {
                await ElMessageBox.confirm(
                    '确定要删除该账号吗？',
                    '警告',
                    {
                        confirmButtonText: '确认删除',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }
                )

                const response = await axios.post(
                    `${config.BASE_URL}/store/api/gzh_auth_management.php`,
                    {
                        action: 'delete',
                        id: id,
                        store_id: this.storeId,
                        user_id: this.userId
                    }
                )

                if (response.data.success) {
                    ElMessage.success('删除成功')
                    this.fetchAccountList()
                } else {
                    ElMessage.error(response.data.error || '删除失败')
                }
            } catch (error) {
                if (error !== 'cancel') {
                    const errorMsg = error.response?.data?.error || error.message || '删除失败'
                    ElMessage.error(`删除失败: ${errorMsg}`)
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.self-media-training {
    .platform-selection {
        margin-bottom: 24px;
    }

    .platform-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 12px;
    }

    .platform-button {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 16px 12px;
        background: hsla(0, 0%, 100%, .1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        color: #e0e6ff;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
            background: rgba(255, 112, 72, 0.2);
            border-color: #ff7048;
            transform: translateY(-2px);
        }

        &.active {
            background: #ff7048;
            border-color: #ff7048;
            color: #fff;
        }

        .platform-icon {
            font-size: 24px;
            margin-bottom: 8px;
        }

        .platform-name {
            font-size: 12px;
            font-weight: 500;
        }
    }

    .single-subject-warning {
        width: 100%;
        margin-bottom: 10px;
    }

    .license-details {
        display: flex;
        gap: 20px;
        background: #f0f7ff;
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
    }

    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .license-info {
        display: flex;
        gap: 20px;
        background: #f0f7ff;
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
    }

    .license-item {
        display: flex;
        align-items: center;

        .label {
            color: #606266;
            font-weight: 500;
        }

        .value {
            font-weight: 600;
            color: #409eff;
        }
    }

    .operation-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding: 16px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
    }

    .search-section {
        display: flex;
        align-items: center;
    }

    .action-buttons {
        display: flex;
        gap: 12px;
    }

    .account-table {
        border: 1px solid rgba(255, 112, 72, 0.3);
        border-radius: 8px;
        overflow: hidden;

        :deep(.el-table) {
            background: transparent;

            th {
                background: rgba(255, 112, 72, 0.1) !important;
                color: #e0e6ff !important;
                border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
            }

            tr {
                background: rgba(255, 255, 255, 0.05);

                &:hover {
                    background: rgba(255, 255, 255, 0.1) !important;
                }
            }

            td {
                color: #e0e6ff !important;
                border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
            }
        }
    }

    .rpa-auth-buttons {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .pagination-container {
        margin-top: 24px;
        display: flex;
        justify-content: flex-end;
    }

    .form-tip {
        font-size: 12px;
        color: #909399;
        margin-top: 6px;
    }

    .empty-value {
        color: #909399;
        font-style: italic;
    }

    .password-preview {
        font-family: monospace;
        background-color: #f5f7fa;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 13px;
    }

    .qr-code-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 20px;

        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 200px;

            span {
                margin-top: 10px;
            }
        }

        .qr-code-image {
            width: 200px;
            height: 200px;
            margin-bottom: 20px;
            border: 1px solid #eee;
        }

        .verification-code-section,
        .password-section {
            margin-top: 15px;
            width: 100%;

            .el-input {
                width: 100%;
            }
        }

        .qr-instructions {
            margin-top: 15px;
            text-align: center;
            color: #606266;
            font-size: 14px;

            p {
                margin: 5px 0;
            }
        }
    }

    .auth-instruction {
        font-size: 14px;
        margin: 10px 0;
        text-align: center;

        strong {
            font-weight: bold;
            color: #ff7048;
        }
    }
}


.precautions-btn {
    background: linear-gradient(135deg, #f9a826 0%, #ff7048 100%) !important;
    border: none !important;
    font-weight: bold !important;

    &:hover {
        background: linear-gradient(135deg, #ff7048 0%, #f9a826 100%) !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(249, 168, 38, 0.4);
    }
}

.precautions-content {
    .common-precautions {
        p {
            margin: 8px 0;
            line-height: 1.6;
            color: #606266;
        }
    }

    .platform-links {
        margin-top: 20px;

        .platform-link-item {
            margin-bottom: 15px;
            padding: 12px;
            background: #f8f9fa;
            border-radius: 6px;
            border-left: 4px solid #ff7048;

            .platform-header {
                display: flex;
                align-items: center;
                margin-bottom: 8px;

                .platform-icon {
                    font-size: 20px;
                    margin-right: 10px;
                }

                .platform-name {
                    font-weight: bold;
                    color: #303133;
                }
            }

            .platform-link {
                a {
                    display: flex;
                    align-items: center;
                    color: #409eff;
                    text-decoration: none;
                    font-size: 14px;

                    &:hover {
                        color: #ff7048;
                        text-decoration: underline;
                    }

                    .el-icon {
                        margin-right: 6px;
                    }
                }
            }
        }
    }
}

// 响应式适配
@media (max-width: 768px) {
    .action-buttons {
        flex-wrap: wrap;
        gap: 10px;

        .precautions-btn {
            order: -1; // 在移动端让注意事项按钮显示在最前面
            width: 100%;
        }
    }
}

// 响应式设计
@media (max-width: 768px) {
    .self-media-training {
        .platform-buttons {
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
            gap: 8px;
        }

        .platform-button {
            padding: 12px 8px;

            .platform-icon {
                font-size: 20px;
            }

            .platform-name {
                font-size: 11px;
            }
        }

        .section-header {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
        }

        .operation-bar {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
        }

        .search-section {
            width: 100%;

            .el-input {
                width: 100%;
                margin-right: 0;
                margin-bottom: 12px;
            }
        }

        .action-buttons {
            width: 100%;
            justify-content: space-between;
        }
    }
}
</style>